import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import {
  ApprovalStatus as Approval_Status_Enum,
  type ApprovalStatus,
} from '@risksmart-app/domain/src/types/consts/approval-status';
import type { ApprovalLevelRow } from '@risksmart-app/drizzle/src/dto';
import { useEffect, useMemo } from 'react';
import {
  type ChangeRequestPresentation,
  useGetChangeRequestsByParentSubscription,
} from 'src/hooks/queries/change-request/useGetChangeRequestsByParentSubscription';
import { useGetLivePendingChangeRequestsSubscription } from 'src/hooks/queries/change-request/useGetLivePendingChangeRequestsSubscription';
import type { LabelledIdArray } from 'src/rbac/types';

import type { ChangeRequestResponses } from '@/utils/changeRequestUtils';
import {
  getActiveLevelId,
  getCurrentApprovers,
  getCurrentLevel,
  getMaxLevel,
  getNextApprovers,
  levelOrderKey,
} from '@/utils/changeRequestUtils';

import { useGetPendingChangeRequests } from './queries';

export type ObjectWithApprovals = {
  Id: string;
};

type ApprovalCheckChangeRequest = {
  ChangeRequestStatus: ApprovalStatus;
  responses: {
    Approved?: boolean | null;
    approver: {
      OwnerApprover?: boolean | null;
      level?: {
        Id: string;
        ApprovalRuleType: string;
        SequenceOrder?: number | null;
        approval?: {
          Id?: string;
          ParentId?: string | null;
          Workflow?: string;
          InFlightEditRule?: string;
        } | null;
      } | null;
      user?: { Id?: string | null } | null;
      group?: { users: { UserId: string }[] } | null;
    };
  }[];
};

type UseChangeRequestResult = {
  pendingChangeRequests: ChangeRequestPresentation[];
  pendingDeleteRequests: ChangeRequestPresentation[];
  isActiveApprover: (
    changeRequest: ApprovalCheckChangeRequest & ChangeRequestWithOwners,
    ownerIds?: string[]
  ) => boolean;
  getCurrentApprovers: (responses: ChangeRequestResponses) => LabelledIdArray;
  getNextApprovers: (responses: ChangeRequestResponses) => LabelledIdArray;
  getCurrentLevel: (responses: ChangeRequestResponses) => number;
  getMaxLevel: (responses: ChangeRequestResponses) => number;
  activeLevelId: (changeRequest: ApprovalCheckChangeRequest) => null | string;
  canAmendChangeRequest: (changeRequest: ChangeRequestPresentation) => boolean;
  changeRequests: ChangeRequestPresentation[];
  loading: boolean;
};

/**
 * An owner grant row as the approval surfaces select it. `user` is never read;
 * it is declared so the register's DISPLAY `owners` relation — which selects
 * `user` and no `UserId` — stays assignable to this row.
 */
type OwnerGrant = {
  UserId?: string | null;
  user?: object | null;
  user_group?: { users: { UserId: string }[] } | null;
};

/**
 * A node carrying owner grants. The record page's fragment names the relation
 * `owners`; the register and my-items documents alias it `currentUserOwners`
 * because `owners` already exists on those nodes as the display relation.
 */
type OwnerNode = {
  currentUserOwners?: OwnerGrant[] | null;
  owners?: OwnerGrant[] | null;
};

type ChangeRequestWithOwners = {
  parent?:
    | (OwnerNode & {
        ObjectType: string;
        documentFile?: { parent?: OwnerNode | null } | null;
        issue_assessment?: { parent?: OwnerNode | null } | null;
        acceptance?: { parents: { risk?: OwnerNode | null }[] } | null;
      })
    | null;
};

const ownerUserIds = (node: OwnerNode | null | undefined) =>
  (node?.currentUserOwners ?? node?.owners)?.flatMap((owner) => [
    ...(owner.UserId ? [owner.UserId] : []),
    ...(owner.user_group?.users.map((user) => user.UserId) ?? []),
  ]);

export const getOwners = (
  changeRequest: ChangeRequestWithOwners
): string[] | undefined => {
  const parent = changeRequest.parent;

  if (parent?.ObjectType === 'document_file') {
    return ownerUserIds(parent.documentFile?.parent);
  }
  if (parent?.ObjectType === 'issue_assessment') {
    return ownerUserIds(parent.issue_assessment?.parent);
  }
  if (parent?.ObjectType === 'acceptance') {
    return parent.acceptance?.parents.flatMap(
      (acceptanceParent) => ownerUserIds(acceptanceParent.risk) ?? []
    );
  }

  return ownerUserIds(parent);
};

export const computeActiveLevelId = (
  changeRequest: ApprovalCheckChangeRequest
) => getActiveLevelId(changeRequest.responses);

/**
 * A level the approval-steps panel renders. `Id` keys the per-level response
 * group; `ApprovalRuleType` is carried for parity with the configured-levels
 * row (the panel does not currently read it, but downstream consumers / tests
 * may rely on the shape matching `ApprovalLevelRow`).
 */
export type DisplayLevel = {
  Id: string;
  ApprovalRuleType: string;
};

/**
 * The subset of an approver-response shape `getDisplayLevels` reads. A
 * structural type (not `ChangeRequestPartsFragment['responses'][number]`) so
 * the helper is callable with any response carrying the level chain — the full
 * fragment is a superset and assigns cleanly.
 */
type DisplayLevelResponse = {
  approver: {
    level?: {
      Id?: string;
      SequenceOrder?: number | null;
      ApprovalRuleType?: string | null;
      approval?: { ParentId?: string | null } | null;
    } | null;
  };
};

/**
 * Resolve the ordered set of levels the approval-steps panel renders for a CR
 * (RSP-6437).
 *
 * Every level PRESENT on the CR's responses is rendered — never dropped into the
 * panel's "No Level" bucket (which shows no approve/reject controls). All levels
 * are ordered by the SAME `levelOrderKey` the active-level walk uses
 * (specific-first, then SequenceOrder), so the panel's display order — and its
 * "Level N" labels — stay aligned with `computeActiveLevelId`'s active-level
 * selection. That key reproduces the backend's `getApprovalLevels` ordering
 * (specific-then-global), so the configured-levels case is unchanged from
 * RSP-6087; `configuredLevels` is now only consulted for each level's
 * `ApprovalRuleType`.
 *
 * Why a response level can be missing from `configuredLevels`: the lookup
 * resolves a SINGLE (workflow, parentId) pair, and the panel derives that
 * parentId from the first response carrying a non-null approval ParentId. A CR
 * whose responses span more than one approval parent — e.g. a per-entity
 * approval configured on the document PLUS the org-wide GLOBAL approval (the
 * exact mixed config in RSP-6437) — can therefore have configured levels that
 * miss some of its own response levels. Before this, those responses fell into
 * "No Level" and an approver eligible only via the missed level (e.g. a member
 * of the global approval group) saw the CR but no approve/reject buttons.
 */
export const getDisplayLevels = (
  responses: DisplayLevelResponse[],
  configuredLevels: ApprovalLevelRow[] | undefined
): DisplayLevel[] => {
  // First response seen per level — carries the level chain `levelOrderKey`
  // needs (SequenceOrder + approval.ParentId), de-duplicating shared levels and
  // preserving first-seen order as the stable-sort tiebreaker.
  const levelById = new Map<
    string,
    DisplayLevelResponse['approver']['level']
  >();
  for (const response of responses) {
    const level = response.approver.level;
    if (level?.Id && !levelById.has(level.Id)) {
      levelById.set(level.Id, level);
    }
  }

  // Index configured levels by Id once so the per-level `ApprovalRuleType`
  // lookup below is O(1) rather than a linear `find` over `configuredLevels`
  // for every rendered level.
  const configuredLevelById = new Map(
    configuredLevels?.map((configured) => [configured.Id, configured])
  );

  return [...levelById.entries()]
    .sort(([, levelA], [, levelB]) => {
      const [globalA, seqA] = levelOrderKey(levelA);
      const [globalB, seqB] = levelOrderKey(levelB);

      return globalA - globalB || seqA - seqB;
    })
    .map(([levelId, level]) => ({
      Id: levelId,
      ApprovalRuleType:
        configuredLevelById.get(levelId)?.ApprovalRuleType ??
        level?.ApprovalRuleType ??
        '',
    }));
};

export const useChangeRequests = (
  objectWithApprovals?: ObjectWithApprovals
): UseChangeRequestResult => {
  const { user, isLoading } = useRisksmartUser();
  const userId = user?.userId;
  // ALL change-request reads go through the holding-pattern subscription hooks
  // (`use*Subscription`): trpc ON → polled tRPC feed mapped V3→V2 (the only
  // surface that reshapes the V3 `RequestedChanges` blob — plain id arrays — into
  // the legacy `Owners`/`tags`/`departments` rows the forms were built against,
  // RSP-6099); trpc OFF → the legacy Hasura subscription. No raw Apollo here —
  // the poll→tRPC-SSE swap (RSP-4320) lives in `createSubscriptionHook`.
  const { data, refetch } = useGetPendingChangeRequests({
    queryArgs: { parentId: objectWithApprovals?.Id ?? '' },
    shouldSkip: !objectWithApprovals?.Id,
  });

  // `data` (a QUERY hook) and `liveData` (a SUBSCRIPTION hook) intentionally
  // read the SAME pending-CR data through two transports — this mirrors the V2
  // design they replace: a one-shot `GetPendingChangeRequests` query plus a live
  // `GetLivePendingChangeRequests` subscription. The query is the authoritative
  // snapshot; the subscription is the live channel. The effect below bridges
  // them: when the live channel drains to empty but the snapshot still shows
  // rows (the merge consumer just settled the CR on another actor), it refetches
  // the snapshot so the two converge. Under `trpc` ON both currently poll the
  // same procedure (no SSE transport yet, RSP-4320) — a known redundancy kept
  // deliberately so flag-OFF behaviour and this convergence are unchanged; it
  // collapses to one feed when the real tRPC subscription transport lands.
  const { data: liveData, loading: liveLoading } =
    useGetLivePendingChangeRequestsSubscription({
      queryArgs: { parentId: objectWithApprovals?.Id ?? '' },
      shouldSkip: !objectWithApprovals?.Id,
    });
  const { data: byParentData, loading } =
    useGetChangeRequestsByParentSubscription({
      queryArgs: { parentId: objectWithApprovals?.Id ?? '' },
      shouldSkip: !objectWithApprovals?.Id,
    });
  const allChangeRequests = byParentData?.change_request;

  useEffect(() => {
    if (
      liveData?.change_request &&
      liveData.change_request.length < 1 &&
      (data?.change_request.length ?? 0) > 0
    ) {
      refetch();
    }
  }, [liveData, data, refetch]);

  const activeLevelId = computeActiveLevelId;

  const isActiveApprover = (
    changeRequest: ApprovalCheckChangeRequest & ChangeRequestWithOwners,
    ownerIds: string[] | undefined = getOwners(changeRequest)
  ) => {
    const levelId = activeLevelId(changeRequest);
    const responses = changeRequest.responses.filter(
      (response) => response.approver.level?.Id === levelId
    );

    const isReferencedActiveApprover = responses.some(
      (response) =>
        (response.approver.user?.Id === userId ||
          response.approver.group?.users.some(
            (user) => user?.UserId === userId
          )) &&
        response.Approved === null
    );
    const ownerApprover = responses.some(
      (response) =>
        response.approver.OwnerApprover && response.Approved === null
    );
    const isOwner = ownerIds?.includes(userId!) ?? false;

    return (
      ((ownerApprover && isOwner) || isReferencedActiveApprover) &&
      changeRequest.ChangeRequestStatus === Approval_Status_Enum.Pending
    );
  };

  const canAmendChangeRequest = (changeRequest: ChangeRequestPresentation) => {
    const levelId = activeLevelId(changeRequest);
    const level = changeRequest.responses.find(
      (response) => response.approver.level?.Id === levelId
    )?.approver.level;
    const rule = level?.approval?.InFlightEditRule;

    switch (rule) {
      case 'everyone':
        return true;
      case 'approvers':
        return isActiveApprover(changeRequest);
      case 'noone':
        return false;
      default:
        return false;
    }
  };

  const pendingChangeRequests = useMemo(
    () =>
      liveData?.change_request.filter((cr) => cr.Type === 'update') ??
      data?.change_request.filter((cr) => cr.Type === 'update') ??
      [],
    [data?.change_request, liveData?.change_request]
  );
  const pendingDeleteRequests = useMemo(
    () =>
      liveData?.change_request.filter((cr) => cr.Type === 'delete') ??
      data?.change_request.filter((cr) => cr.Type === 'delete') ??
      [],
    [data?.change_request, liveData?.change_request]
  );

  const changeRequests = useMemo(
    () => allChangeRequests ?? [],
    [allChangeRequests]
  );

  return {
    loading: loading || liveLoading || isLoading,
    isActiveApprover,
    activeLevelId,
    canAmendChangeRequest,
    getCurrentApprovers,
    getNextApprovers,
    getCurrentLevel,
    getMaxLevel,
    pendingDeleteRequests,
    pendingChangeRequests,
    changeRequests,
  };
};
